import type { Msg } from '@halo/chat-sdk';
import { useMessageExpireTimer } from '@halo/ui-chat-react';
import { ParticleList } from '@halo/ui-chat-react';
import type { ReactNode } from 'react';
import { itemExpireContext, useItemExpireContext } from './context';

export interface ItemExpireContextProps {
  message: Msg;
  children: ReactNode;
}

export const ItemExpireContext = memo(function ItemExpireContext({
  message,
  children,
}: ItemExpireContextProps) {
  const { displayTime, time } = useMessageExpireTimer({
    message: message!,
  });

  const alwaysShowTime = useMemo(() => {
    return time < 24 * 60 * 60 * 1000;
  }, [time]);

  const value = useMemo(
    () => ({ time, displayTime, alwaysShowTime }),
    [time, displayTime, alwaysShowTime]
  );

  return <itemExpireContext.Provider value={value}>{children}</itemExpireContext.Provider>;
});

export interface ItemWithExpireAnimateProps {
  doExpireAnimation?: boolean;
  onExpireAnimationEnd?: () => Promise<void>;
  animateDirection: 'left' | 'right';
  children: ReactNode;
}

export const ItemWithExpireAnimate = memo(function ItemWithExpireAnimate({
  doExpireAnimation,
  onExpireAnimationEnd,
  animateDirection,
  children,
}: ItemWithExpireAnimateProps) {
  const [expire, setExpire] = useState(false);

  const { time } = useItemExpireContext();

  useLayoutEffect(() => {
    if (time === 0) {
      setExpire(true);
    }
  }, [time]);

  useLayoutEffect(() => {
    if (doExpireAnimation) {
      setExpire(true);
    }
  }, [doExpireAnimation]);

  if (!expire) {
    return children;
  }

  return (
    <ParticleList
      color="#ccc"
      type="rectangle"
      style="fill"
      direction={animateDirection}
      onComplete={onExpireAnimationEnd}
    >
      {children}
    </ParticleList>
  );
});
